/**
 * Snap system bootstrap + entity registry sync.
 *
 * Two structural gaps made OSNAPs inert before this module existed:
 *  1. getSnapManager() snapshotted useSnapStore only on first creation, so
 *     SnapsDialog / SnapSettings toggles never reached the live manager.
 *  2. registerEntity/unregisterEntity had no callers, so the manager's
 *     entity map stayed empty and only grid candidates were ever produced.
 *
 * startSnapSystem() wires both: a store subscription keeps settings live,
 * and a mirror of the CAD entity ledger keeps the snap geometry current.
 * The drafting hooks commit through applyOsnap() so the marker the
 * user sees is exactly the point the click lands on.
 */

import * as THREE from 'three';
import { useAppStore, type Entity } from '../../state/store';
import { useSnapStore, getSnapManager, type EntitySnapData, type SnapPoint } from './SnapManager';

let started = false;

/** Convert one CAD entity payload into snap geometry (Z-up world). */
function entitySnapData(entity: Entity): EntitySnapData | null {
    const d = entity.data ?? {};
    const v = (a: unknown): THREE.Vector3 | null =>
        Array.isArray(a) && a.length >= 2 ? new THREE.Vector3(a[0], a[1], a[2] ?? 0) : null;
    switch (entity.type) {
        case 'line': {
            const s = v(d.start), e = v(d.end);
            return s && e ? { type: 'line', start: s, end: e } : null;
        }
        case 'polyline': {
            const raw: unknown[] = Array.isArray(d.vertices) ? d.vertices : [];
            const vs = raw.map(v).filter((p): p is THREE.Vector3 => !!p);
            return vs.length >= 2 ? { type: 'polyline', vertices: vs, closed: !!d.closed } : null;
        }
        case 'curve': {
            // NURBS curve: treat its control polygon as the snap skeleton.
            const raw: unknown[] = Array.isArray(d.controlPoints) ? d.controlPoints
                : Array.isArray(d.points) ? d.points : [];
            const vs = raw.map(v).filter((p): p is THREE.Vector3 => !!p);
            return vs.length >= 2 ? { type: 'polyline', vertices: vs, closed: !!d.closed } : null;
        }
        case 'circle': {
            const c = v(d.center);
            return c && typeof d.radius === 'number' ? { type: 'circle', center: c, radius: d.radius } : null;
        }
        case 'arc': {
            const c = v(d.center);
            return c && typeof d.radius === 'number'
                ? { type: 'arc', center: c, radius: d.radius, startAngle: d.startAngle ?? 0, endAngle: d.endAngle ?? Math.PI * 2 }
                : null;
        }
        case 'ellipse': {
            const c = v(d.center);
            return c && typeof d.radiusA === 'number'
                ? { type: 'circle', center: c, radius: (d.radiusA + (d.radiusB ?? d.radiusA)) / 2 }
                : null;
        }
        case 'rectangle': {
            const c1 = v(d.corner1), c2 = v(d.corner2);
            return c1 && c2 ? { type: 'rectangle', corner1: c1, corner2: c2 } : null;
        }
        case 'box': {
            // NurbsTools box stores corner1/corner2 (XY) + height.
            const c1 = v(d.corner1), c2 = v(d.corner2);
            if (c1 && c2) {
                const center = c1.clone().add(c2).multiplyScalar(0.5);
                center.z = ((d.height as number) ?? 0) / 2;
                return {
                    type: 'box',
                    center,
                    size: new THREE.Vector3(
                        Math.abs(c2.x - c1.x), Math.abs(c2.y - c1.y), (d.height as number) ?? 1
                    ),
                };
            }
            const p = v(d.position);
            const s = v(d.size);
            return p && s ? { type: 'box', center: p, size: s } : null;
        }
        case 'point': {
            const p = v(d.position);
            return p ? { type: 'point', position: p } : null;
        }
        default:
            return null;
    }
}

/** Rebuild the manager's entity map from the current CAD ledger. */
export function syncSnapEntities(): void {
    const manager = getSnapManager();
    const { entities, layers } = useAppStore.getState();
    // Frozen layers drop out of snap regeneration too (LAYFRZ contract:
    // excluded from both rendering and snapping).
    const frozenLayers = new Set(
        Object.values(layers).filter((l) => l.frozen).map((l) => l.name)
    );
    const live = new Set<string>();
    Object.values(entities).forEach((e: Entity) => {
        if (!e.visible || e.locked || frozenLayers.has(e.layer)) return;
        const data = entitySnapData(e);
        if (data) {
            manager.registerEntity(e.id, data);
            live.add(e.id);
        }
    });
    // Drop stale entries (deleted entities or ones that lost geometry).
    manager.registeredIds().forEach((id) => {
        if (!live.has(id)) manager.unregisterEntity(id);
    });
}

/** Boot the live settings mirror + entity ledger subscription (idempotent). */
export function startSnapSystem(): void {
    if (started) return;
    started = true;

    // Settings: any toggle in SnapsDialog / SnapSettings / snapToggle command
    // reaches the manager immediately.
    useSnapStore.subscribe((state) => {
        getSnapManager().updateSettings({
            enabled: state.enabled,
            aperture: state.aperture,
            gridSize: state.gridSize,
            gridSnapEnabled: state.gridSnapEnabled,
            types: state.types,
        });
    });

    // Entities: resync the snap ledger whenever the entity map changes.
    useAppStore.subscribe((state, prev) => {
        if (state.entities === prev.entities) return;
        syncSnapEntities();
    });

    // Initial settings snapshot (the manager was created with boot values).
    const s = useSnapStore.getState();
    getSnapManager().updateSettings({ enabled: s.enabled, aperture: s.aperture, gridSize: s.gridSize, gridSnapEnabled: s.gridSnapEnabled, types: s.types });

    syncSnapEntities();
}

/**
 * Commit point resolution shared by the drafting hooks: ask the live snap
 * manager for the best OSNAP candidate at the cursor's ground position.
 * When one exists, the click must land exactly there — the same query the
 * drafting feedback pipeline runs for the visual marker and the statusbar
 * readout, so marker and committed coordinate can never disagree.
 * `base` (last committed point, if any) unlocks the direction-dependent
 * perpendicular mode, exactly like the classic OSNAP contract. Returns null
 * when nothing is nearby (or snapping is off).
 */
export function resolveOsnap(
    cursor: THREE.Vector3,
    camera: THREE.Camera,
    renderer: THREE.WebGLRenderer,
    base?: THREE.Vector3 | null
): SnapPoint | null {
    return getSnapManager().getBestSnap(cursor, camera, renderer, base);
}

/**
 * Vector3-in/Vector3-out flavor of resolveOsnap for callers that only want
 * the resolved point (falls through to the input point when nothing is
 * nearby or snapping is off). Same underlying query, so it stays consistent
 * with the marker pipeline.
 */
export function applyOsnap(
    cursor: THREE.Vector3,
    camera: THREE.Camera,
    renderer: THREE.WebGLRenderer,
    base?: THREE.Vector3 | null
): THREE.Vector3 {
    const snap = resolveOsnap(cursor, camera, renderer, base);
    return snap ? snap.position : cursor;
}
